export interface AdminDivisions {
  level1: string | null
  level2: string | null
}

interface ArcGisQueryResponse {
  features?: Array<{ attributes: Record<string, string | null> }>
  error?: unknown
}

interface Resolution {
  divisions: AdminDivisions
  failed: boolean
}

const GADM_URL = 'https://wwf-sight-maps.org/arcgis/rest/services/Global/Administrative_Boundaries_GADM/MapServer'
const COUNTRY_LAYER = 0
const FIRST_LEVEL_LAYER = 1
const NOT_FOUND: AdminDivisions = { level1: null, level2: null }
const COORDINATE_DECIMALS = 4
const REQUEST_TIMEOUT_MS = 10_000

const cache = new Map<string, Promise<AdminDivisions>>()

export function clearAdminDivisionsCache() {
  cache.clear()
}

// Plain fetch on purpose: the shared http client would send the Auth0 token to a third party.
async function queryLayer(layer: number, outFields: string, latitude: number, longitude: number) {
  const params = new URLSearchParams({
    f: 'json',
    geometry: JSON.stringify({ x: longitude, y: latitude, spatialReference: { wkid: 4326 } }),
    geometryType: 'esriGeometryPoint',
    spatialRel: 'esriSpatialRelIntersects',
    outFields,
    returnGeometry: 'false'
  })
  const response = await fetch(`${GADM_URL}/${layer}/query?${params}`, { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) })
  if (!response.ok) throw new Error(`GADM layer ${layer} answered ${response.status}`)
  const body = (await response.json()) as ArcGisQueryResponse
  if (body.error) throw new Error(`GADM layer ${layer} query failed`)
  return body.features?.[0]?.attributes ?? null
}

// Layer 1 carries NAME_0 and NAME_1, so one query is enough almost everywhere. Some points
// (e.g. small islands) are covered by a country polygon but by no first-level polygon, so
// the country is then resolved from layer 0.
async function resolve(latitude: number, longitude: number): Promise<Resolution> {
  let failed = false
  try {
    const attributes = await queryLayer(FIRST_LEVEL_LAYER, 'NAME_0,NAME_1', latitude, longitude)
    if (attributes) {
      return { divisions: { level1: attributes.NAME_0 || null, level2: attributes.NAME_1 || null }, failed }
    }
  } catch {
    failed = true
  }
  try {
    const attributes = await queryLayer(COUNTRY_LAYER, 'NAME_0', latitude, longitude)
    return { divisions: { level1: attributes?.NAME_0 || null, level2: null }, failed }
  } catch {
    return { divisions: NOT_FOUND, failed: true }
  }
}

export function getAdminDivisionsByPoint(latitude: number, longitude: number): Promise<AdminDivisions> {
  // The same rounded point feeds the cache key and the request, so a cached answer matches what would be asked.
  const roundedLatitude = Number(latitude.toFixed(COORDINATE_DECIMALS))
  const roundedLongitude = Number(longitude.toFixed(COORDINATE_DECIMALS))
  const key = `${roundedLatitude},${roundedLongitude}`
  const cached = cache.get(key)
  if (cached) return cached

  const pending = resolve(roundedLatitude, roundedLongitude).then(({ divisions, failed }) => {
    if (failed) cache.delete(key)
    return divisions
  })
  cache.set(key, pending)
  return pending
}
